import { FC, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { WifiOff, Mail, MessageCircle, Webhook, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../modules/auth';
import { PageCard, SectionLabel, TOKENS } from '../../../components/design-system';
import { useRecentOfflineBoxes, OfflineWindow, RECENT_OFFLINE_KEY_PREFIX } from '../hooks/useRecentOfflineBoxes';
import { subscribePresenceChanged, subscribeNotificationEvents } from '../transports/WebSocketTransport';

const WINDOW_OPTIONS: Array<{ hours: OfflineWindow; label: string }> = [
  { hours: 24, label: '24h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
];

export const RecentlyOfflineTab: FC = () => {
  const { currentUser } = useAuth();
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined;
  const qc = useQueryClient();

  const [windowHours, setWindowHours] = useState<OfflineWindow>(24);
  const [showRecovered, setShowRecovered] = useState(true);

  // email_only foi removido a pedido — mostra todas as caixas
  // notificadas independente do canal que efetivamente entregou.
  // Os chips por canal (Email/WA/Hook) na linha já sinalizam o que
  // saiu, o filtro em cima era ruído.
  const { data = [], isLoading } = useRecentOfflineBoxes({
    company_id: companyId,
    window_hours: windowHours,
    include_recovered: showRecovered,
  });

  // Dois canais de invalidação:
  //   1. presence_changed — trata "voltou online" e "caiu" via gateway
  //      (LeafletMap já subscribe pra o mesmo evento). Faz refetch se
  //      qualquer caixa mudou presença: bruto mas simples e rápido.
  //   2. box_offline_notified / box_recovered_notified — vem da API
  //      via Redis pub/sub (PR #204/#27). Latência <1s do momento
  //      que o email foi enviado até a linha aparecer na aba.
  useEffect(() => {
    const unsubPresence = subscribePresenceChanged(() => {
      qc.invalidateQueries([...RECENT_OFFLINE_KEY_PREFIX]);
    });
    const unsubNotif = subscribeNotificationEvents((type) => {
      if (type === 'box_offline_notified' || type === 'box_recovered_notified') {
        qc.invalidateQueries([...RECENT_OFFLINE_KEY_PREFIX]);
      }
    });
    return () => {
      unsubPresence();
      unsubNotif();
    };
  }, [qc]);

  const { pending, recovered } = useMemo(() => {
    const pending: typeof data = [];
    const recovered: typeof data = [];
    for (const row of data) (row.recovered ? recovered : pending).push(row);
    return { pending, recovered };
  }, [data]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <PageCard>
        <SectionLabel>Sem comunicação</SectionLabel>
        <div style={{
          fontSize: 13, color: TOKENS.color.muted, lineHeight: 1.5, marginTop: 4, marginBottom: TOKENS.spacing.md,
        }}>
          Caixas que dispararam notificação por perda de comunicação. Cada linha mostra o
          registro mais recente e o estado atual (ainda sem comunicação ou já restabelecida).
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {WINDOW_OPTIONS.map((opt) => {
              const active = windowHours === opt.hours;
              return (
                <span
                  key={opt.hours} role='button'
                  onClick={() => setWindowHours(opt.hours)}
                  style={{
                    padding: '4px 12px', borderRadius: TOKENS.radius.chip,
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                    cursor: 'pointer',
                    background: active ? TOKENS.color.action : TOKENS.color.surface,
                    color: active ? '#fff' : TOKENS.color.text,
                  }}
                >{opt.label}</span>
              );
            })}
          </div>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: TOKENS.color.text }}>
            <input type='checkbox' checked={showRecovered} onChange={(e) => setShowRecovered(e.target.checked)} />
            Mostrar restabelecidas
          </label>

          <div style={{ marginLeft: 'auto', fontSize: 11, color: TOKENS.color.muted, fontWeight: 700, letterSpacing: 0.3 }}>
            {pending.length} pendente{pending.length === 1 ? '' : 's'} · {recovered.length} restabelecida{recovered.length === 1 ? '' : 's'}
          </div>
        </div>
      </PageCard>

      {isLoading && (
        <PageCard><div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>Carregando…</div></PageCard>
      )}

      {!isLoading && data.length === 0 && (
        <PageCard>
          <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>
            Nenhuma notificação de perda de comunicação nas últimas {WINDOW_OPTIONS.find(w => w.hours === windowHours)?.label ?? `${windowHours}h`}.
          </div>
        </PageCard>
      )}

      {pending.length > 0 && (
        <PageCard>
          <SectionLabel>Sem comunicação no momento</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {pending.map((row) => <OfflineRow key={`${row.box_id}-${row.notified_at}`} row={row} />)}
          </div>
        </PageCard>
      )}

      {showRecovered && recovered.length > 0 && (
        <PageCard>
          <SectionLabel>Comunicação restabelecida</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
            {recovered.map((row) => <OfflineRow key={`${row.box_id}-${row.notified_at}`} row={row} />)}
          </div>
        </PageCard>
      )}
    </div>
  );
};

// ------------ Row ------------

const OfflineRow: FC<{ row: ReturnType<typeof useRecentOfflineBoxes>['data'] extends readonly (infer T)[] | undefined ? T : never }> = ({ row }) => {
  const notifiedAgo = timeAgo(row.notified_at);
  const currentSeenAgo = row.current_last_seen_at ? timeAgo(row.current_last_seen_at) : 'nunca reportou';

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      padding: '10px 12px', background: TOKENS.color.surface,
      borderRadius: TOKENS.radius.chip, fontSize: 12,
      borderLeft: `3px solid ${row.recovered ? TOKENS.color.severity.low.text : TOKENS.color.severity.medium.text}`,
      opacity: row.recovered ? 0.85 : 1,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 700, color: TOKENS.color.text, display: 'flex', alignItems: 'center', gap: 6 }}>
          {row.recovered
            ? <CheckCircle2 size={13} color={TOKENS.color.severity.low.text} />
            : <WifiOff size={13} color={TOKENS.color.severity.medium.text} />}
          CMB {row.cmb_number ?? '—'}
          {row.company_name && (
            <span style={{ color: TOKENS.color.muted, fontWeight: 500, marginLeft: 4 }}>· {row.company_name}</span>
          )}
          {row.project_name && (
            <span style={{ color: TOKENS.color.muted, fontWeight: 500 }}>· {row.project_name}</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: TOKENS.color.muted }}>
          Notificada {notifiedAgo}
          {row.minutes_threshold != null && ` · limite ${row.minutes_threshold} min`}
          {' · '}último heartbeat {currentSeenAgo}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <DeliveryChip icon={<Mail size={12} />} label='Email' info={row.delivery.email} />
        <DeliveryChip icon={<MessageCircle size={12} />} label='WA' info={row.delivery.whatsapp} />
        <DeliveryChip icon={<Webhook size={12} />} label='Hook' info={row.delivery.webhook} />
      </div>
    </div>
  );
};

const DeliveryChip: FC<{ icon: React.ReactNode; label: string; info: { ok: boolean; recipients?: string[] } | null }> = ({ icon, label, info }) => {
  if (!info) return null;
  const color = info.ok ? TOKENS.color.severity.low.text : TOKENS.color.muted;
  const title = info.ok
    ? `${label}: enviado${info.recipients?.length ? ` para ${info.recipients.slice(0, 3).join(', ')}${info.recipients.length > 3 ? '…' : ''}` : ''}`
    : `${label}: falhou`;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: TOKENS.radius.chip,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
        background: info.ok ? TOKENS.color.severity.low.bg : TOKENS.color.surface,
        color,
        border: `1px solid ${info.ok ? 'transparent' : TOKENS.color.border}`,
      }}
    >
      {icon} {label}
    </span>
  );
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `há ${diffSec}s`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}
